// The Assembly Graph is the single source of truth. The 3D scene, the BOM panel,
// and all animation are pure functions of (graph, station). Build nothing that
// bypasses this object.

export type StationId = "SA1" | "SA2" | "SA3" | "MA1" | "MA2" | "QC1" | "QC2";

// Authoritative process order. Used to compute "stations <= current".
export const STATION_ORDER: StationId[] = [
  "SA1",
  "SA2",
  "SA3",
  "MA1",
  "MA2",
  "QC1",
  "QC2",
];

export type Branch = "piston_sub" | "main" | "qc";

export type StatusOverlay = "torque" | "final";

export interface StationDef {
  label: string;
  /** Occurrence IDs introduced at this station (become "new / highlighted"). */
  addsOccurrences: string[];
  /** BOM item IDs added at this station. */
  bomItems: number[];
  branch: Branch;
  /** QC stations add no geometry; they annotate the complete assembly. */
  statusOverlay?: StatusOverlay;
}

export interface OccurrenceDef {
  /** glTF node name (stable, emitted by the offline convert.py step). */
  meshRef: string;
  /** 4x4 row-major placement matrix (from STEP). */
  finalTransform: number[];
  /** Authored start offset for the "in" tween, relative to finalTransform. */
  installOffset: [number, number, number];
  /** Join key to a BOM row. */
  bomItemId: number;
  /**
   * Sub-assembly PREVIEW part: visible only while a piston_sub (SA) station is
   * current, and dropped once main-assembly stations begin. Used for the crank
   * "throw" slice shown at SA2 — a stand-in for the crankshaft that would otherwise
   * overlap the real full crankshaft added at MA1.
   */
  previewOnly?: boolean;
  /**
   * Delay (ms) before this part's "in" tween starts when it enters at a station.
   * Lets a single station stagger its parts so distinct components read as separate
   * steps (e.g. crankshaft first, then the engine block later at MA1). The resting
   * state is unchanged, so renderState() stays pure. Default 0.
   */
  installDelayMs?: number;
  /**
   * For a preview/stand-in part: instead of animating OUT when it leaves, hold it in
   * place and instantly hide it the moment the named occurrence finishes installing
   * ("lands"). Used for the SA2 crank-throw shortcut, which should vanish exactly when
   * the real crankshaft arrives at MA1 — no fly-away exit, since it was never really
   * a separate part.
   */
  hideOnLandingOf?: string;
}

export interface BomItem {
  name: string;
  qty: number;
  partNumber?: string;
  description?: string;
  revision?: string;
}

export interface AssemblyConfig {
  /**
   * When true, MA2 installs all 12 pistons (each an occurrence whose meshRef
   * points at a distinct glTF node). When false, a single representative piston.
   */
  animateAllPistons?: boolean;
  /** Highlight strategy. 'opacity' dims prior parts; 'color' emissive-tints new. */
  highlightMode?: "opacity" | "color";
}

export interface AssemblyGraph {
  assemblyId: string;
  config?: AssemblyConfig;
  stations: Record<StationId, StationDef>;
  occurrences: Record<string, OccurrenceDef>;
  bom: Record<number, BomItem>;
}

/** Index of a station within STATION_ORDER, or -1 if unknown. */
export function stationIndex(id: string): number {
  return STATION_ORDER.indexOf(id as StationId);
}

/** Type guard for a raw string coming from an attribute. */
export function isStationId(id: string): id is StationId {
  return STATION_ORDER.includes(id as StationId);
}
